import { useState } from "react";
import MainLayout from "@/components/feature/MainLayout";
import GisMap2D from "./components/GisMap2D";
import GisMap3D from "./components/GisMap3D";
import GisStatsBar from "./components/GisStatsBar";
import PipelineFlow from "./components/PipelineFlow";
import GisRegionPanel from "./components/GisRegionPanel";
import { gisMarkers } from "@/mocks/gis";

type MapMode = "2d" | "3d" | "topology";

const filterOptions = [
  { key: "resident", label: "居民用户", icon: "ri-home-4-line", color: "bg-green-100 text-green-700 border-green-200" },
  { key: "commercial", label: "工商业", icon: "ri-building-2-line", color: "bg-orange-100 text-orange-700 border-orange-200" },
  { key: "alarm", label: "告警点", icon: "ri-alarm-warning-line", color: "bg-red-100 text-red-700 border-red-200" },
  { key: "station", label: "调压站", icon: "ri-building-4-line", color: "bg-blue-100 text-blue-700 border-blue-200" },
];

export default function GisPage() {
  const [mapMode, setMapMode] = useState<MapMode>("2d");
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [showList, setShowList] = useState(false);
  const [showRegionPanel, setShowRegionPanel] = useState(false);

  const toggleFilter = (key: string) => {
    setFilterTypes(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const filteredList = gisMarkers.filter(m => {
    const typeMatch = filterTypes.length === 0 || filterTypes.includes(m.type);
    const searchMatch = !searchText || m.name.includes(searchText) || m.address.includes(searchText);
    return typeMatch && searchMatch;
  });

  return (
    <MainLayout>
      <div className="flex flex-col h-[calc(100vh-64px)] gap-3">
        {/* 顶部标题栏 */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">GIS 一张图</h2>
            <p className="text-sm text-gray-400 mt-0.5">燃气管网与用户地理信息可视化平台</p>
          </div>
          <div className="flex items-center gap-2">
            {/* 模式切换 */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {([
                { key: "2d", label: "2D 地图" },
                { key: "3d", label: "3D 视图" },
                { key: "topology", label: "管网拓扑" },
              ] as { key: MapMode; label: string }[]).map(mode => (
                <button
                  key={mode.key}
                  onClick={() => setMapMode(mode.key)}
                  className={`px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer whitespace-nowrap font-medium ${
                    mapMode === mode.key ? "bg-white text-gray-800" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowList(!showList)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer whitespace-nowrap transition-colors ${
                showList ? "bg-[#E8531A] text-white border-[#E8531A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              <i className="ri-list-check text-sm"></i>
              点位列表
            </button>
            <button
              onClick={() => setShowRegionPanel(!showRegionPanel)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer whitespace-nowrap transition-colors ${
                showRegionPanel ? "bg-[#E8531A] text-white border-[#E8531A]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              <i className="ri-bar-chart-grouped-line text-sm"></i>
              区域统计
            </button>
          </div>
        </div>

        {/* 统计栏 */}
        <div className="flex-shrink-0">
          <GisStatsBar />
        </div>

        {/* 筛选栏 - only show for map modes */}
        {mapMode !== "topology" && (
          <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-gray-400"></i>
              </div>
              <input
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder="搜索用户名/地址"
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#E8531A]/50 w-52 bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">图层筛选：</span>
              {filterOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => toggleFilter(opt.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border cursor-pointer whitespace-nowrap transition-all ${
                    filterTypes.includes(opt.key)
                      ? opt.color + " font-medium"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <i className={`${opt.icon} text-xs`}></i>
                  {opt.label}
                </button>
              ))}
              {filterTypes.length > 0 && (
                <button
                  onClick={() => setFilterTypes([])}
                  className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  清除筛选
                </button>
              )}
            </div>
            <span className="text-xs text-gray-400 ml-auto">显示 {filteredList.length} 个点位</span>
          </div>
        )}

        {/* 管网拓扑说明栏 */}
        {mapMode === "topology" && (
          <div className="flex items-center gap-4 flex-shrink-0 bg-[#0a1628] rounded-xl px-4 py-2.5">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
              <span className="text-white/70 text-xs">管线流向动画实时运行</span>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/50">
              <span>调压站: 5个</span>
              <span>管线: 7条</span>
              <span>阀门: 7个</span>
              <span>压力监测: 5个</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-red-400 text-xs">1处超压告警</span>
            </div>
          </div>
        )}

        {/* 主体区域 */}
        <div className="flex gap-3 flex-1 min-h-0">
          {/* 地图/拓扑图 */}
          <div className="flex-1 min-h-0 relative overflow-hidden rounded-xl">
            {mapMode === "2d" && <GisMap2D filterTypes={filterTypes} searchText={searchText} />}
            {mapMode === "3d" && <GisMap3D filterTypes={filterTypes} searchText={searchText} />}
            {mapMode === "topology" && <PipelineFlow />}

            {/* 地图模式标签 */}
            {mapMode !== "topology" && (
              <div className={`absolute top-3 left-3 px-3 py-1.5 rounded-lg text-xs font-medium z-[999] ${
                mapMode === "3d" ? "bg-gray-900/80 text-white" : "bg-white/90 text-gray-700 border border-gray-200"
              }`}>
                <i className={`${mapMode === "2d" ? "ri-map-2-line" : "ri-building-3-line"} mr-1`}></i>
                {mapMode === "2d" ? "标准地图" : "3D 柱状视图"}
              </div>
            )}

            {/* Region Panel */}
            {showRegionPanel && (
              <GisRegionPanel onClose={() => setShowRegionPanel(false)} />
            )}
          </div>

          {/* 点位列表侧边栏 */}
          {showList && mapMode !== "topology" && (
            <div className="w-72 flex-shrink-0 bg-white rounded-xl border border-gray-100 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">点位列表</span>
                <span className="text-xs text-gray-400">{filteredList.length} 个</span>
              </div>
              <div className="flex-1 overflow-y-auto">
                {filteredList.map(marker => (
                  <div
                    key={marker.id}
                    className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        marker.type === "alarm" ? "bg-red-100" :
                        marker.type === "station" ? "bg-orange-100" :
                        marker.type === "commercial" ? "bg-orange-100" : "bg-green-100"
                      }`}>
                        <i className={`text-xs ${
                          marker.type === "alarm" ? "ri-alarm-warning-line text-red-500" :
                          marker.type === "station" ? "ri-building-4-line text-[#E8531A]" :
                          marker.type === "commercial" ? "ri-building-2-line text-orange-600" :
                          "ri-home-4-line text-green-600"
                        }`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-sm font-medium text-gray-800 truncate">{marker.name}</span>
                          {marker.alarmLevel && (
                            <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                              marker.alarmLevel === "紧急" ? "bg-red-100 text-red-600" :
                              marker.alarmLevel === "严重" ? "bg-orange-100 text-orange-600" :
                              "bg-yellow-100 text-yellow-600"
                            }`}>{marker.alarmLevel}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 truncate">{marker.address}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs ${marker.status === "正常" ? "text-green-600" : "text-red-500"}`}>
                            {marker.status}
                          </span>
                          {marker.monthlyUsage !== undefined && (
                            <span className="text-xs text-gray-400">{marker.monthlyUsage.toLocaleString()} m³/月</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
